import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/utils';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Home, ExternalLink, Check, Sparkles, BedDouble, Ruler } from 'lucide-react';

const groupByProperty = alerts => Object.values(alerts.reduce((acc, a) => {
  const g = acc[a.property.id] || (acc[a.property.id] = { property: a.property, alerts: [] });
  g.alerts.push(a);
  return acc;
}, {}));

const PropertyAlert = ({ group, onSeen, onOpenLead }) => {
  const p = group.property;
  const n = group.alerts.length;
  return (
    <div data-testid={`wasi-alert-${p.id}`} className="bg-[#16302B] rounded-xl border border-white/5 overflow-hidden">
      <div className="flex gap-3 p-3">
        <div className="w-24 h-20 shrink-0 rounded-lg overflow-hidden bg-[#0A1F1A]">
          {p.image ? <img src={p.image} alt={p.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20"><Home size={22} /></div>}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm text-white font-medium line-clamp-2">{p.title}</p>
          <p className="text-xs text-white/50">{p.zone} · <span className="text-[#A38560] tabular-nums">{formatCOP(p.price)}</span></p>
          <div className="flex items-center gap-3 text-[11px] text-white/50">
            <span className="flex items-center gap-1"><BedDouble size={12} />{p.bedrooms}</span>
            <span className="flex items-center gap-1"><Ruler size={12} />{p.area ? `${p.area} m²` : '—'}</span>
            <a href={p.link} target="_blank" rel="noopener noreferrer" data-testid={`wasi-alert-link-${p.id}`} className="flex items-center gap-1 text-[#A38560] hover:text-[#C4A87C]">Ver en WASI <ExternalLink size={11} /></a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/5 px-3 py-2 space-y-1.5">
        <p className="text-[11px] uppercase tracking-widest text-white/40">Le sirve a {n} cliente{n !== 1 ? 's' : ''}</p>
        {group.alerts.map(a => (
          <div key={a.id} className="flex items-center justify-between gap-2">
            <Link to={`/crm/${a.lead_id}`} onClick={onOpenLead} data-testid={`wasi-alert-lead-${a.id}`} className="text-sm text-white hover:text-[#A38560] truncate">
              {a.lead_name}{a.features_total > 0 && <span className="text-[11px] text-white/40"> · {a.features_matched}/{a.features_total} características</span>}
            </Link>
            <button onClick={() => onSeen([a.id])} data-testid={`wasi-alert-seen-${a.id}`}
              className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] bg-white/5 border border-white/10 text-white/60 hover:text-[#6BCB77] hover:border-[#6BCB77]/40 transition-colors">
              <Check size={12} />Visto
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const WasiAlertsModal = () => {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get('/crm/wasi-alerts').then(a => { setAlerts(a); setOpen(a.length > 0); }).catch(() => {});
  }, []);

  const markSeen = async ids => {
    try {
      await api.post('/crm/wasi-alerts/seen', { ids });
      const rest = alerts.filter(a => !ids.includes(a.id));
      setAlerts(rest);
      if (!rest.length) setOpen(false);
    } catch { toast.error('No se pudo marcar como visto'); }
  };

  const groups = groupByProperty(alerts);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="wasi-alerts-modal" className="bg-[#0A1F1A] border-[#1A3B32] text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit text-lg flex items-center gap-2"><Sparkles size={18} className="text-[#A38560]" />Inmuebles nuevos para tus clientes</DialogTitle>
          <p className="text-xs text-white/50">Se publicaron en WASI inmuebles que se ajustan a la búsqueda de tus clientes.</p>
        </DialogHeader>
        <div className="space-y-3">
          {groups.map(g => <PropertyAlert key={g.property.id} group={g} onSeen={markSeen} onOpenLead={() => setOpen(false)} />)}
        </div>
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
          <button onClick={() => setOpen(false)} data-testid="wasi-alerts-later" className="px-4 py-2 rounded-xl text-sm text-white/60 hover:text-white">Ver después</button>
          <button onClick={() => markSeen(alerts.map(a => a.id))} data-testid="wasi-alerts-seen-all"
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D]">Marcar todos como vistos</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
